import { useState, useEffect, useRef } from 'react'
import {
  CloseButton,
  Input,
  NativeSelect,
  Switch,
  Button,
  VStack,
  HStack,
  Alert,
  Box,
  Text,
  Field,
  Dialog,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { FiUpload, FiTrash2 } from 'react-icons/fi'
import { useUpdateUser, useCommitteeOptions, useUploadPortrait, useDeletePortrait } from '../../hooks/useAdminUsers'
import { UserAvatar } from './UserAvatar'
import { ResponsiveModal } from '../shared'
import type { AdminUser, UpdateUserRequest } from '../../types/admin'

interface UserEditModalProps {
  user: AdminUser | null
  isOpen: boolean
  onClose: () => void
}

export function UserEditModal({ user, isOpen, onClose }: UserEditModalProps) {
  const updateUser = useUpdateUser()
  const uploadPortrait = useUploadPortrait()
  const deletePortrait = useDeletePortrait()
  const { data: committeeOptions } = useCommitteeOptions()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [role, setRole] = useState('')
  const [committeeId, setCommitteeId] = useState('')
  const [isActive, setIsActive] = useState(true)

  useEffect(() => {
    if (user) {
      setFirstName(user.firstName)
      setLastName(user.lastName)
      setEmail(user.email)
      setPhone(user.phone || '')
      setRole(user.role)
      setCommitteeId(user.committeeId || '')
      setIsActive(user.isActive)
    }
  }, [user])

  const handleSubmit = async () => {
    if (!user) return

    const data: UpdateUserRequest = {
      firstName,
      lastName,
      email,
      phone: phone || undefined,
      role: role as UpdateUserRequest['role'],
      isActive,
    }

    // Handle committee assignment
    if (committeeId && committeeId !== (user.committeeId || '')) {
      data.committeeId = committeeId
    } else if (!committeeId && user.committeeId) {
      data.clearCommittee = true
    }

    try {
      await updateUser.mutateAsync({ id: user.id, data })
      toaster.create({
        title: 'User updated',
        description: `${firstName} ${lastName} has been updated.`,
        type: 'success',
        duration: 3000,
      })
      onClose()
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to update user'
      toaster.create({
        title: 'Error',
        description: message,
        type: 'error',
        duration: 5000,
      })
    }
  }

  const handlePortraitChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !user) return

    try {
      await uploadPortrait.mutateAsync({ id: user.id, file })
      toaster.create({ title: 'Portrait uploaded', type: 'success', duration: 2000 })
    } catch {
      toaster.create({ title: 'Failed to upload portrait', type: 'error', duration: 4000 })
    }
    // Reset so the same file can be re-selected if needed
    e.target.value = ''
  }

  const handleDeletePortrait = async () => {
    if (!user) return
    try {
      await deletePortrait.mutateAsync(user.id)
      toaster.create({ title: 'Portrait removed', type: 'success', duration: 2000 })
    } catch {
      toaster.create({ title: 'Failed to remove portrait', type: 'error', duration: 4000 })
    }
  }

  return (
    <ResponsiveModal isOpen={isOpen} onClose={onClose} size="md">
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content>
          <Dialog.Header>Edit User</Dialog.Header>
          <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
          <Dialog.Body>
            <VStack gap={4}>
              {!!updateUser.error && (
                <Alert.Root status="error" borderRadius="md">
                  <Alert.Indicator />
                  Failed to update user. Please try again.
                </Alert.Root>
              )}

              {/* Portrait */}
              {user && (
                <Field.Root>
                  <Field.Label fontSize="sm" color="gray.600">Portrait</Field.Label>
                  <HStack gap={4}>
                    <UserAvatar
                      userId={user.id}
                      firstName={firstName || user.firstName}
                      portraitUrl={user.portraitUrl}
                      size="lg"
                    />
                    <VStack align="start" gap={2}>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        loading={uploadPortrait.isPending}><FiUpload />{user.portraitUrl ? 'Replace' : 'Upload'}</Button>
                      {user.portraitUrl && (
                        <Button
                          size="sm"
                          variant="ghost"
                          colorPalette="red"
                          onClick={handleDeletePortrait}
                          loading={deletePortrait.isPending}><FiTrash2 />Remove
                                                </Button>
                      )}
                      <Text fontSize="xs" color="gray.400">
                        JPEG, PNG, WebP · max 5 MB
                      </Text>
                    </VStack>
                    <Box display="none">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        onChange={handlePortraitChange}
                      />
                    </Box>
                  </HStack>
                </Field.Root>
              )}

              <HStack gap={4} w="full">
                <Field.Root>
                  <Field.Label fontSize="sm" color="gray.600">First Name</Field.Label>
                  <Input
                    size="sm"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </Field.Root>
                <Field.Root>
                  <Field.Label fontSize="sm" color="gray.600">Last Name</Field.Label>
                  <Input
                    size="sm"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </Field.Root>
              </HStack>

              <Field.Root>
                <Field.Label fontSize="sm" color="gray.600">Email</Field.Label>
                <Input
                  size="sm"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Field.Root>

              <Field.Root>
                <Field.Label fontSize="sm" color="gray.600">Phone</Field.Label>
                <Input
                  size="sm"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </Field.Root>

              <Field.Root>
                <Field.Label fontSize="sm" color="gray.600">Role</Field.Label>
                <NativeSelect.Root size="sm">
                  <NativeSelect.Field value={role} onChange={(e) => setRole(e.target.value)}>
                    <option value="Organizer">Organizer</option>
                    <option value="Admin">Admin</option>
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Field.Root>

              <Field.Root>
                <Field.Label fontSize="sm" color="gray.600">Committee</Field.Label>
                <NativeSelect.Root size="sm">
                  <NativeSelect.Field
                    value={committeeId}
                    onChange={(e) => setCommitteeId(e.target.value)}
                    placeholder="Unassigned">
                    {committeeOptions?.map((c) => (
                      <option key={c.id} value={c.id}>{c.label}</option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Field.Root>

              <Field.Root display="flex" alignItems="center">
                <Field.Label fontSize="sm" color="gray.600" mb={0}>Active</Field.Label>
                <Switch.Root
                  checked={isActive}
                  onCheckedChange={(e) => setIsActive(e.checked === true)}
                  colorPalette="brand"
                >
                  <Switch.HiddenInput />
                  <Switch.Control>
                    <Switch.Thumb />
                  </Switch.Control>
                </Switch.Root>
              </Field.Root>
            </VStack>
          </Dialog.Body>

          <Dialog.Footer>
            <HStack gap={3}>
              <Button variant="outline" size="sm" onClick={onClose}>
                Cancel
              </Button>
              <Button
                colorPalette="brand"
                size="sm"
                onClick={handleSubmit}
                loading={updateUser.isPending}
              >
                Save Changes
              </Button>
            </HStack>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </ResponsiveModal>
  );
}
